import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { X, CircleNotch } from '@phosphor-icons/react';
import { IDEA_CATEGORIES, getCategoryLabel } from 'config/ideaCategories';
import { createIdea, updateIdea } from 'api/companyMapping';
import { useFeedback } from 'contexts/feedback';

const initialFormState = {
    title: '',
    category: 'altro',
    impact: 5,
    ease: 5,
    description: ''
};

export default function AddIdeaPopup({
    visible,
    setVisible,
    editingIdea = null
}) {
    const { conversationId } = useParams();
    const [form, setForm] = useState(initialFormState);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();
    const { setSuccess, setError } = useFeedback();

    const isEditing = !!editingIdea;

    // Create idea mutation
    const { mutate: createIdeaMutate, status: createStatus } = useMutation({
        mutationKey: ['createCompanyMappingIdea', conversationId],
        mutationFn: createIdea,
        onSuccess: () => {
            queryClient.invalidateQueries(['company-mapping-ideas', conversationId]);
            setSuccess('Idea aggiunta con successo');
            handleClose();
        },
        onError: () => {
            setError('Errore durante l\'aggiunta dell\'idea');
        }
    });

    // Update idea mutation
    const { mutate: updateIdeaMutate, status: updateStatus } = useMutation({
        mutationKey: ['updateCompanyMappingIdea'],
        mutationFn: updateIdea,
        onSuccess: () => {
            queryClient.invalidateQueries(['company-mapping-ideas', conversationId]);
            setSuccess('Idea aggiornata con successo');
            handleClose();
        },
        onError: () => {
            setError('Errore durante l\'aggiornamento dell\'idea');
        }
    });

    const isPending = createStatus === 'pending' || updateStatus === 'pending';

    useEffect(() => {
        if (editingIdea) {
            setForm({
                title: editingIdea.title || '',
                category: editingIdea.category || 'altro',
                impact: editingIdea.impact || 5,
                ease: editingIdea.ease || (editingIdea.difficulty ? 10 - editingIdea.difficulty + 1 : 5),
                description: editingIdea.description || ''
            });
        } else {
            setForm(initialFormState);
        }
        setErrors({});
    }, [editingIdea, visible]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: name === 'impact' || name === 'ease' ? Number(value) : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.title.trim()) {
            newErrors.title = 'Il titolo è obbligatorio';
        }
        if (form.impact < 1 || form.impact > 10) {
            newErrors.impact = 'L\'impatto deve essere tra 1 e 10';
        }
        if (form.ease < 1 || form.ease > 10) {
            newErrors.ease = 'La facilità deve essere tra 1 e 10';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate() || isPending) return;

        if (isEditing) {
            const id = editingIdea._id || editingIdea.id;
            const updatedIdea = {
                ...form,
                categoryLabel: getCategoryLabel(form.category)
            };
            updateIdeaMutate({ id, body: updatedIdea });
        } else {
            const newIdea = {
                ...form,
                categoryLabel: getCategoryLabel(form.category),
                source: 'manual',
                conversation: conversationId
            };
            createIdeaMutate({ conversationId, body: newIdea });
        }
    };

    const handleClose = () => {
        if (isPending) return;
        setVisible(false);
        setForm(initialFormState);
        setErrors({});
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={handleClose}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
                    <h2 className="text-lg font-semibold text-black/90">
                        {isEditing ? 'Modifica Idea' : 'Aggiungi Idea'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors"
                        disabled={isPending}
                    >
                        <X size={20} weight="bold" className="text-black/50" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-black/70 mb-2">
                            Titolo *
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            placeholder="Es: Automazione fatture con OCR"
                            disabled={isPending}
                            className={`w-full px-4 py-3 rounded-xl border ${
                                errors.title ? 'border-red-500' : 'border-black/10'
                            } focus:outline-none focus:border-black/30 transition-colors disabled:opacity-50`}
                        />
                        {errors.title && (
                            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                        )}
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-black/70 mb-2">
                            Categoria
                        </label>
                        <select
                            name="category"
                            value={form.category}
                            onChange={handleChange}
                            disabled={isPending}
                            className="w-full px-4 py-3 rounded-xl border border-black/10 focus:outline-none focus:border-black/30 transition-colors bg-white disabled:opacity-50"
                        >
                            {IDEA_CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Impact & Ease sliders */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-black/70 mb-2">
                                Impatto Aziendale
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    name="impact"
                                    min="1"
                                    max="10"
                                    value={form.impact}
                                    onChange={handleChange}
                                    disabled={isPending}
                                    className="flex-1 h-2 bg-black/10 rounded-lg appearance-none cursor-pointer accent-black disabled:opacity-50"
                                />
                                <span className="w-8 text-center font-semibold text-black/80">
                                    {form.impact}
                                </span>
                            </div>
                            {errors.impact && (
                                <p className="text-red-500 text-sm mt-1">{errors.impact}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-black/70 mb-2">
                                Facilità di Implementazione
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    name="ease"
                                    min="1"
                                    max="10"
                                    value={form.ease}
                                    onChange={handleChange}
                                    disabled={isPending}
                                    className="flex-1 h-2 bg-black/10 rounded-lg appearance-none cursor-pointer accent-black disabled:opacity-50"
                                />
                                <span className="w-8 text-center font-semibold text-black/80">
                                    {form.ease}
                                </span>
                            </div>
                            {errors.ease && (
                                <p className="text-red-500 text-sm mt-1">{errors.ease}</p>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-black/70 mb-2">
                            Descrizione (opzionale)
                        </label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Descrivi brevemente l'idea e i suoi benefici..."
                            disabled={isPending}
                            className="w-full px-4 py-3 rounded-xl border border-black/10 focus:outline-none focus:border-black/30 transition-colors resize-none disabled:opacity-50"
                        />
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                            className="flex-1 px-4 py-3 rounded-xl border border-black/10 text-black/70 font-medium hover:bg-black/5 transition-colors disabled:opacity-50"
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            disabled={isPending}
                            className="flex-1 px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-black/90 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                        >
                            {isPending && <CircleNotch size={18} className="animate-spin" />}
                            {isEditing ? 'Salva Modifiche' : 'Aggiungi Idea'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
